import { VisitorFilter } from '../../../config'

/**
 * Shared vocabulary for the WHAT popover's Filter tab (#870 slice 5).
 *
 * The toolkit's WHAT field has no separate "N active dimensions" idea: its
 * pill text IS `countActiveFilters()` (searchbar-pro.js:1646-1660), which
 * counts every selected chip individually plus one each for bedrooms,
 * bathrooms, and an off-default price range. Ported here so the widget's pill
 * reads the same way, with two widget-only additions: the keyword box (the
 * toolkit has no such control — name search is its own tab) and `featured`,
 * which has no control in the pane but can arrive from a URL or a visitor
 * default and would otherwise filter results the pill claims are unfiltered.
 */

export type WhatSectionVisibility = {
  keyword: boolean
  price: boolean
  bedrooms: boolean
  bathrooms: boolean
  types: boolean
  amenities: boolean
}

/**
 * The ONLY suppression the count is allowed to honor. Hiding a section does
 * not stop its value being sent: `stripHiddenRoomFilters` (data.ts:188-201) is
 * the whole of the request-side stripping, and it removes bedrooms/bathrooms
 * on `showBedroomsFilter`/`showBathroomsFilter` alone. Anything else the admin
 * hides still filters the results, so the count must still report it —
 * otherwise the pill reads "no filters" while results are cut, and the
 * per-field clear and "Clear all" (both gated on the count) never appear.
 */
export type WhatCountableRooms = {
  bedrooms: boolean
  bathrooms: boolean
}

export type WhatCountableFilters = Pick<
  VisitorFilter,
  'keywords' | 'price_min' | 'price_max' | 'bedrooms' | 'bathrooms' | 'featured' | 'types' | 'amenities' | 'amenities_and'
>

/** Split a stored CSV of Central term IDs into its ids, dropping blanks. */
export function csvIds(value: string | undefined): string[] {
  return (value ?? '').split(',').filter(Boolean)
}

/** Join ids back to the stored CSV shape, or undefined when the set is empty. */
export function idsToCsv(ids: string[]): string | undefined {
  return ids.length > 0 ? ids.join(',') : undefined
}

export function countActiveWhatFilters(vf: WhatCountableFilters, rooms: WhatCountableRooms): number {
  let n = 0
  // Unconditional, exactly like `featured`: every one of these still reaches
  // the properties request whether or not its section renders.
  n += csvIds(vf.types).length
  n += csvIds(vf.amenities ?? vf.amenities_and).length
  if (vf.price_min != null || vf.price_max != null) n++
  if ((vf.keywords ?? '').trim() !== '') n++
  if (vf.featured) n++
  // The two the request path really does drop.
  if (rooms.bedrooms && (vf.bedrooms ?? 0) > 0) n++
  if (rooms.bathrooms && (vf.bathrooms ?? 0) > 0) n++
  return n
}

/** WHAT field value line: the count replaces the placeholder once anything is on. */
export function whatFieldLabel(count: number, placeholder: string): string {
  if (count <= 0) return placeholder
  return count === 1 ? '1 filter' : `${count} filters`
}

/**
 * WHAT field placeholder (toolkit parity, search-bar.php:698-700): the Describe
 * tab changes what the field offers, so it changes what the field says.
 */
export function whatPlaceholder(aiConcierge: boolean): string {
  return aiConcierge ? 'Describe or filter' : 'Add filters'
}
